// React
import { useEffect, useState, useMemo } from 'react';

// Generic tools
import _ from 'lodash';
import CurrencyList from 'currency-list';

// MUI imports
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import TextField from '@mui/material/TextField';

// Local imports
import { FieldTypes } from '../../../FieldTypes.js';

function ExcelPasteDialog(props) {
    const { open, onClose, excelData, transportationMethod, organization } = props;
    const [ selectedRows, setSelectedRows ] = useState([]);
    const [ fieldNameColumn, setFieldNameColumn ] = useState(null);
    const [ fieldValueColumn, setFieldValueColumn ] = useState(null);
    const [ step, setStep ] = useState(0);

    useEffect(() => {
        setSelectedRows(excelData.map(() => true));
    }, [excelData]);


    function onDone({ result }) {
        onClose({ result });
    }

    function _onClose() {
        onClose(null);
    }

    function onNext() {
        setStep(step => step + 1);
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth='lg'>
            <DialogTitle>Excel Paste</DialogTitle>
            <DialogContent>
                <Stepper activeStep={step} sx={{ mb: 2 }} alternativeLabel>
                    <Step completed={step > 0}>
                        <StepButton onClick={() => setStep(0)}>Step 1 - Pick columns</StepButton>
                    </Step>
                    <Step completed={step > 1}>
                        <StepButton disabled={step < 1} onClick={() => setStep(1)}>Step 2 - Review results</StepButton>
                    </Step>
                </Stepper>
                {step === 0 && (
                    <Step1 
                        excelData={excelData}
                        selectedRows={selectedRows}
                        setSelectedRows={setSelectedRows}
                        fieldNameColumn={fieldNameColumn}
                        setFieldNameColumn={setFieldNameColumn}
                        fieldValueColumn={fieldValueColumn}
                        setFieldValueColumn={setFieldValueColumn} 
                        onNext={onNext}
                    />
                )}
                {step === 1 && (
                    <Step2 
                        excelData={excelData}
                        selectedRows={selectedRows}
                        fieldNameColumn={fieldNameColumn}
                        fieldValueColumn={fieldValueColumn}
                        transportationMethod={transportationMethod}
                        organization={organization}
                        onDone={onDone}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}

function Step1(props) {
    const { excelData, selectedRows, setSelectedRows, fieldNameColumn, setFieldNameColumn, 
        fieldValueColumn, setFieldValueColumn, onNext } = props;

    const [ errors, setErrors ] = useState([]);

    function _onNext() {
        let er = [];
        if (fieldNameColumn === -1) er.push('fieldnamecolumn');
        if (fieldValueColumn === -1) er.push('fieldvaluecolumn');
        if (fieldNameColumn === fieldValueColumn) er = _.uniq([...er, 'fieldnamecolumn', 'fieldvaluecolumn']);

        setErrors(er);
        if (er.length === 0) onNext();
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
                <TableContainer component={Paper}>
                    <Table size='small'>
                        <TableHead>
                            <TableRow sx={{ borderTop: '1px solid #ccc' }}>
                                <TableCell sx={{ borderRight: '1px solid #ccc', width: '80px' }} />
                                {_.range(_.max(excelData.map(row => row.length))).map(index => (
                                    <TableCell key={index} sx={{ borderRight: '1px solid #ccc' }}>Column {index + 1}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {excelData.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell sx={{ borderRight: '1px solid #ccc' }}>
                                        <Checkbox checked={!!selectedRows[index]} onChange={e => setSelectedRows(rows => {
                                            const newRows = [...rows];
                                            newRows[index] = e.target.checked;
                                            return newRows;
                                        })} />
                                    </TableCell>
                                    {row.map((cell, index) => (
                                        <TableCell key={index} sx={{ borderRight: '1px solid #ccc' }}>{cell}</TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <Box sx={{ flex: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant='body1' sx={{ whiteSpace: 'nowrap' }}>Field name column:</Typography>
                <Select
                    value={fieldNameColumn}
                    onChange={e => {
                        setFieldNameColumn(e.target.value);
                        setErrors(errors => errors.filter(error => error !== 'fieldnamecolumn'));
                    }}
                    error={errors.includes('fieldnamecolumn')}
                >
                    <MenuItem value={null}><em>Select a column</em></MenuItem>
                    {_.range(_.max(excelData.map(row => row.length))).map(index => (
                        <MenuItem key={index} value={index}>Column {index + 1}</MenuItem>
                    ))}
                </Select>
                <Typography variant='caption' color='error' sx={{ whiteSpace: 'nowrap', visibility: errors.includes('fieldnamecolumn') ? 'visible' : 'hidden' }}>Please select a field name column</Typography>
                <Typography variant='body1' sx={{ whiteSpace: 'nowrap' }}>Field price column:</Typography>
                <Select
                    value={fieldValueColumn}
                    onChange={e => {
                        setFieldValueColumn(e.target.value);
                        setErrors(errors => errors.filter(error => error !== 'fieldvaluecolumn'));
                    }}
                    error={errors.includes('fieldvaluecolumn')}
                >
                    <MenuItem value={null}><em>Select a column</em></MenuItem>
                    {_.range(_.max(excelData.map(row => row.length))).map(index => (
                        <MenuItem key={index} value={index}>Column {index + 1}</MenuItem>
                    ))}
                </Select>
                <Typography variant='caption' color='error' sx={{ whiteSpace: 'nowrap', visibility: errors.includes('fieldvaluecolumn') ? 'visible' : 'hidden' }}>Please select a field value column</Typography>
                <Button variant='contained' onClick={_onNext} disabled={errors.length > 0}>Next</Button>
            </Box>
        </Box>
    );
}

function Step2(props) {
    const { excelData, selectedRows, fieldNameColumn, fieldValueColumn, 
        transportationMethod, organization, onDone } = props;
    const [ data, setData ] = useState([]);

    const currencies = useMemo(() => _.values(CurrencyList.getAll('en_US')), []);

    useEffect(() => {
        setData(_.compact(excelData.filter((row, index) => selectedRows[index]).map(row => {
            const name = row[fieldNameColumn];
            const price = row[fieldValueColumn];

            // Using regex, split price to number and currency.
            // Currency might be a symbol before or after the number, or a three letter code.
            // Price might be a floating point number with optional commas.
            const priceMatch = price.match(/^([^\d]*)\s*(\d{1,3}(?:,\d{3})*(?:\.\d+)?|\d+(?:\.\d+)?)\s*([^\d]*)$/);
            if (!priceMatch || priceMatch.length < 4) {
                console.warn('Skipping line:', row);
                return null;
            }
            const number = priceMatch[2].replace(/,/g, '').trim();
            const currency = (priceMatch[1] || priceMatch[3]).trim();

            // Convert currency symbol to currency code using currency-list.
            const currencyCode = (currency === 'US$' ? 'USD' : currencies.find(c => c.symbol === currency)?.code) || currency;

            return {
                name: name,
                price: number,
                currency: currencyCode,
                chargeType: 'flat',
            };
        })));
    }, [excelData, selectedRows, fieldNameColumn, fieldValueColumn]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
                <TableContainer>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Currency</TableCell>
                                <TableCell>Charge Type</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <TextField
                                            fullWidth
                                            value={row.name}
                                            onChange={e => setData(data => {
                                                const newData = [...data];
                                                newData[index].name = e.target.value;
                                                return newData;
                                            })}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            fullWidth
                                            value={row.price}
                                            onChange={e => setData(data => {
                                                const newData = [...data];
                                                newData[index].price = e.target.value;
                                                return newData;
                                            })}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Select
                                            fullWidth
                                            value={row.currency}
                                            onChange={e => setData(data => {
                                                const newData = [...data];
                                                newData[index].currency = e.target.value;
                                                return newData;
                                            })}
                                        >
                                            {currencies.map(k => (
                                                <MenuItem key={k.code} value={k.code}>{k.name} ({k.code})</MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                    <TableCell>
                                        <Select
                                            fullWidth
                                            value={row.chargeType}
                                            onChange={(e) => setData(data => {
                                                const newData = [...data];
                                                newData[index].chargeType = e.target.value;
                                                return newData;
                                            })}
                                        >
                                            {FieldTypes[transportationMethod]?.map(ft => (ft.value === 'string' || ft.value === 'custom' || (_.isFunction(ft.enabled) && !ft.enabled({ organization }))) ? null : <MenuItem key={ft.value} value={ft.value}>{ft.label}</MenuItem>)}
                                        </Select>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <Box sx={{ flex: 0, display: 'flex', flexDirection: 'column', gap: 1, minWidth: '200px', paddingTop: '44px' }}>
                <Typography variant='body2'>Go over the results and click done to add the fields to the opportunity.</Typography>
                <Button variant='contained' onClick={() => onDone({ result: data })}>Done</Button>
            </Box>
        </Box>
    )
}

export default ExcelPasteDialog;